import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Badge, Select, Input, Flex, Heading,
  Spinner, Center, IconButton, Tooltip, Code,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';

const APP_COLOR = { portal: 'teal', dashboard: 'blue', mobile: 'orange', ticketing: 'purple', unknown: 'gray' };

function EventRow({ event }) {
  const [expanded, setExpanded] = useState(false);
  const hasProps = event.properties && Object.keys(event.properties).length > 0;
  return (
    <Box
      bg="white"
      borderRadius="md"
      px={4}
      py={3}
      borderWidth="1px"
      borderColor="gray.100"
      cursor={hasProps ? 'pointer' : 'default'}
      onClick={() => hasProps && setExpanded(v => !v)}
      _hover={hasProps ? { borderColor: 'purple.200', boxShadow: 'sm' } : {}}
      transition="all 0.15s"
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <HStack spacing={2} flex={1}>
          <Badge colorScheme={APP_COLOR[event.app] || 'gray'} fontSize="xs">{event.app}</Badge>
          <Text color="gray.800" fontSize="sm" fontWeight="500">{event.event_type}</Text>
          {event.screen && <Text color="gray.400" fontSize="xs">on {event.screen}</Text>}
        </HStack>
        <HStack spacing={3}>
          {event.tenant_slug && <Text color="gray.500" fontSize="xs">{event.tenant_slug}</Text>}
          {event.user_id && <Text color="gray.400" fontSize="xs">u:{event.user_id}</Text>}
          <Text color="gray.400" fontSize="xs">
            {new Date(event.ts).toLocaleTimeString()}
          </Text>
        </HStack>
      </Flex>
      {expanded && hasProps && (
        <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.100">
          <Code bg="gray.50" color="purple.700" fontSize="xs" p={2} borderRadius="md" display="block" whiteSpace="pre">
            {JSON.stringify(event.properties, null, 2)}
          </Code>
        </Box>
      )}
    </Box>
  );
}

function EventStream({ token, apiBase }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState('');
  const [eventType, setEventType] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, ordering: '-ts' });
      if (app) params.set('app', app);
      if (eventType) params.set('event_type', eventType);
      const resp = await fetch(`${apiBase}/api/v1/events/?${params}`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setEvents(data.results || []);
        setTotal(data.count || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [token, apiBase, app, eventType, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [app, eventType]);

  const pageCount = Math.ceil(total / 100);

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <Heading size="lg" color="gray.800">Event Stream</Heading>
        <Text color="gray.500" mt={1}>Live feed of all incoming telemetry events</Text>
      </Box>

      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text color="gray.500" fontSize="sm">{total.toLocaleString()} events total</Text>
        <HStack>
          <Select
            size="sm"
            bg="white"
            borderColor="gray.200"
            color="gray.700"
            value={app}
            onChange={e => setApp(e.target.value)}
            w="140px"
            borderRadius="md"
          >
            <option value="">All apps</option>
            <option value="portal">Portal</option>
            <option value="dashboard">Dashboard</option>
            <option value="mobile">Mobile</option>
            <option value="ticketing">Ticketing</option>
          </Select>
          <Input
            size="sm"
            bg="white"
            borderColor="gray.200"
            color="gray.700"
            placeholder="Filter event type..."
            _placeholder={{ color: 'gray.400' }}
            value={eventType}
            onChange={e => setEventType(e.target.value)}
            w="180px"
            borderRadius="md"
          />
          <Tooltip label="Refresh">
            <IconButton
              icon={<FiRefreshCw />}
              size="sm"
              colorScheme="purple"
              variant="ghost"
              onClick={load}
              isLoading={loading}
              aria-label="Refresh"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {loading && events.length === 0 ? (
        <Center py={10}><Spinner color="purple.500" /></Center>
      ) : events.length === 0 ? (
        <Center py={10}><Text color="gray.400">No events found</Text></Center>
      ) : (
        <VStack spacing={2} align="stretch">
          {events.map(e => <EventRow key={e.id} event={e} />)}
        </VStack>
      )}

      {pageCount > 1 && (
        <Flex justify="center" gap={2} mt={2}>
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme="purple"
            isDisabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            aria-label="Previous"
            icon={<Text fontSize="xs">◀</Text>}
          />
          <Text color="gray.500" fontSize="sm" alignSelf="center">
            Page {page} of {pageCount}
          </Text>
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme="purple"
            isDisabled={page === pageCount}
            onClick={() => setPage(p => p + 1)}
            aria-label="Next"
            icon={<Text fontSize="xs">▶</Text>}
          />
        </Flex>
      )}
    </VStack>
  );
}

export default EventStream;
